import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { SharedDataService } from '../shared-data.service';
import { LAW, LEGAL_SITUATION, ESTABLISHMENT_TYPE, INVESTMENT_NATURE } from '../laws-and-legal-situation-constants';
import { combineLatest } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FileUploadResult } from '../file-upload/file-upload.component';
import { TabRoutes } from '../modals/tab-routes.enum';
import { PreEstablishmentDataComponent } from '../../features/services/foundation-tabs/pre-establishment-data/pre-establishment-data.component';

interface RequiredDocument {
  id: string;
  label: string;
  preEstablishmentKey: keyof PreEstablishmentDataComponent['preEstablishmentUrls'];
}

@Component({
  selector: 'app-required-documents',
  templateUrl: './required-documents.component.html',
  styleUrls: ['./required-documents.component.scss'],
})
export class RequiredDocumentsComponent implements OnInit, OnDestroy {
  documentsForm!: FormGroup;
  law: any;
  legalSituation: any;
  establishmentType: any;
  investmentNature: any;
  freeZoneType: any;
  @Output() documentsChanged = new EventEmitter<{ [key: string]: string }>();
  showCompanyInfo: boolean = false;
  requiredDocs: RequiredDocument[] = [
    { id: 'tax-card', label: 'البطاقة الضريبية', preEstablishmentKey: 'taxCardUrl' },
    { id: 'commercial-register', label: 'السجل التجاري', preEstablishmentKey: 'commercialRegisterUrl' },
    { id: 'asset-evaluation', label: 'تقييم الحصص العينية', preEstablishmentKey: 'inKindSharesEvaluationUrl' },
    { id: 'partnership-contract', label: 'عقد تكوين شركة الأشخاص', preEstablishmentKey: 'partnershipEstablishmentContractUrl' },
    { id: 'modification-contracts', label: 'عقود التعديل وملخصاتها', preEstablishmentKey: 'amendmentContractsAndSummariesUrl' },
    { id: 'pre-evaluation-minutes', label: 'محضر الشركاء قبل التقييم', preEstablishmentKey: 'partnersMeetingMinutesBeforeEvaluationUrl' },
    { id: 'post-evaluation-minutes', label: 'محضر الشركاء بعد التقييم', preEstablishmentKey: 'partnersMeetingMinutesAfterEvaluationUrl' },
    { id: 'division-decision', label: 'قرار الانقسام', preEstablishmentKey: 'divisionDecisionUrl' },
    { id: 'pre-division-assembly', label: 'محضر الجمعية غير العادية قبل الانقسام', preEstablishmentKey: 'extraordinaryGeneralMeetingMinutesBeforeDivisionUrl' },
    { id: 'post-division-assembly', label: 'محضر الجمعية غير العادية بعد الانقسام', preEstablishmentKey: 'extraordinaryGeneralMeetingMinutesAfterDivisionUrl' },
    { id: 'division-contract-draft', label: 'مشروع عقد التقسيم', preEstablishmentKey: 'draftDivisionContractUrl' },
    { id: 'post-evaluation-assembly', label: 'محضر الجمعية العامة غير العادية بعد التقييم', preEstablishmentKey: 'extraordinaryGeneralMeetingMinutesAfterEvaluationUrl' },
  ];

  uploadedUrls: { [key: string]: string } = {};
  filePath: string;
  private subscriptions: Subscription[] = [];
  isReadOnly: boolean = false; // Default to readonly (no note)

  constructor(private sharedData: SharedDataService, private fb: FormBuilder) {
    this.filePath = TabRoutes.PreEstablishment
  }

  ngOnInit(): void {
    // Subscribe to noteSections from SharedDataService (wizard updates it via getNotes())
    const noteSub = this.sharedData.noteSections$.subscribe((noteSections: string[]) => {
      const stepCode = 'PRE_FOUNDATION';
      const hasNote = noteSections.includes(stepCode);
      const status = this.sharedData.getRequestStatus();
      if (status === 'PENDING_EDIT') {
        if (hasNote === true) {
          this.isReadOnly = false;
        }
        else {
          this.isReadOnly = true;
        }
      } else if (status === 'DRAFT') {
        this.isReadOnly = false;
      } else if (status === '') {
        this.isReadOnly = false;
      }
      else {
        this.isReadOnly = true;
      }
      // Re-validate form if switching to editable mode
      if (!this.isReadOnly) {
        this.documentsForm.markAsUntouched();
      }
      console.log('📝 PRE_FOUNDATION readonly:', this.isReadOnly);
    });
    this.subscriptions.push(noteSub);

    this.sharedData.selectedLaw$.subscribe((law) => {
      this.law = law;
      console.log('📜 Law updated:', law);
      this.buildForm();
    });

    this.sharedData.selectedLegalSituation$.subscribe((situation) => {
      this.legalSituation = situation;
      console.log('🏢 Legal Situation updated:', situation);
      this.buildForm();
    });

    this.sharedData.selectedEstablishmentType$.subscribe((type) => {
      this.establishmentType = type;
      console.log('💼 Investment Type updated:', type);
      this.buildForm();
    });

    this.sharedData.selectedInvestmentNature$.subscribe((nature) => {
      this.investmentNature = nature;
      console.log('🌍 Investment Nature updated:', nature);
      this.buildForm();
    });

    this.sharedData.selectedFreeZoneType$.subscribe((zone) => {
      this.freeZoneType = zone
    })


    // combineLatest([
    //   this.sharedData.isLaw159$,
    //   this.sharedData.isLaw72$,
    //   this.sharedData.selectedLegalSituation$,
    //   this.sharedData.selectedEstablishmentType$,
    //   this.sharedData.selectedInvestmentNature$,
    // ]).subscribe(([isLaw159, isLaw72, situation, establishmentType, investmentNature]) => {

    //   console.log('🔍 Combined Form Values:');
    //   console.log('isLaw159:', isLaw159);
    //   console.log('isLaw72:', isLaw72);
    //   console.log('Legal Situation:', situation);
    //   console.log('Investment Type:', establishmentType);
    //   console.log('Investment Nature:', investmentNature);

    //   this.isLaw159 = isLaw159;
    //   this.isLaw72 = isLaw72;
    //   this.legalSituation = situation;
    //   this.establishmentType = establishmentType;
    //   this.investmentNature = investmentNature;

    //   // rebuild form controls whenever conditions change

    // });

    this.buildForm();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private buildForm(): void {
    const group: any = {};
    this.filteredDocs.forEach(doc => {
      group[doc.id] = [null, this.isReadOnly ? [] : Validators.required];
    });
    this.documentsForm = this.fb.group(group);
  }

  loadInitialData(initialUrls: { [key: string]: string }): void {
    // Clear existing (if any)
    this.uploadedUrls = {};
    // Patch form and uploadedUrls based on filtered docs
    this.filteredDocs.forEach(doc => {
      const url = initialUrls[doc.preEstablishmentKey];
      if (url) {
        this.documentsForm.get(doc.id)?.setValue(url);
        this.uploadedUrls[doc.preEstablishmentKey] = url;
      }
    });
    // Emit the loaded URLs back to parent (in case of updates)
    this.documentsChanged.emit(this.uploadedUrls);
    console.log('📥 Loaded initial documents:', this.uploadedUrls);
  }

  get filteredDocs(): RequiredDocument[] {
    const isJointStock = this.legalSituation?.code === LEGAL_SITUATION.JointStock;
    const isLaw159Or72 = this.law?.code === LAW.Law159 || this.law?.code === LAW.Law72;

    console.log('Filtering required documents with conditions:');
    console.log('isLaw159Or72:', isLaw159Or72);
    console.log('isJointStock:', isJointStock);
    console.log('Establishment Type:', this.establishmentType?.code);
    console.log('Legal Situation:', this.legalSituation?.code);
    console.log('Investment Nature:', this.investmentNature?.code);

    if (isLaw159Or72 && isJointStock && this.establishmentType?.code === ESTABLISHMENT_TYPE.Conversion) {
      return this.requiredDocs.filter(doc =>
        [
          'tax-card',
          'commercial-register',
          'partnership-contract',
          'asset-evaluation',
          'modification-contracts',
          'pre-evaluation-minutes',
          'post-evaluation-minutes',
        ].includes(doc.id)
      );
    }

    if (isLaw159Or72 && isJointStock && this.establishmentType?.code === ESTABLISHMENT_TYPE.Division) {
      return this.requiredDocs.filter(doc =>
        [
          'tax-card',
          'commercial-register',
          'division-decision',
          'asset-evaluation',
          'pre-division-assembly',
          'post-division-assembly',
          'division-contract-draft',
          'post-evaluation-assembly',
        ].includes(doc.id)
      );
    }

    if ((this.legalSituation?.code === LEGAL_SITUATION.LimitedLiability || this.legalSituation?.code === LEGAL_SITUATION.PartnershipByShares) && this.establishmentType?.code === ESTABLISHMENT_TYPE.Conversion) {
      return this.requiredDocs.filter(doc =>
        [
          'tax-card',
          'commercial-register',
          'partnership-contract',
          'modification-contracts',
          'pre-evaluation-minutes'
        ].includes(doc.id)
      );
    }

    if (this.legalSituation?.code === LEGAL_SITUATION.LimitedLiability && this.establishmentType?.code === ESTABLISHMENT_TYPE.Division) {
      return this.requiredDocs.filter(doc =>
        [
          'tax-card',
          'commercial-register',
          'asset-evaluation',
          'division-decision',
          'pre-division-assembly',
          'post-division-assembly',
          'post-evaluation-assembly',
          'division-contract-draft'
        ].includes(doc.id)
      );
    }

    if (
      this.law?.code === LAW.Law72 &&
      (this.legalSituation?.code === LEGAL_SITUATION.GeneralPartnership ||
        this.legalSituation?.code === LEGAL_SITUATION.LimitedPartnership) &&
      this.establishmentType?.code === ESTABLISHMENT_TYPE.DeFacto &&
      (this.investmentNature?.code === INVESTMENT_NATURE.Internal ||
        this.investmentNature?.code === INVESTMENT_NATURE.FreeZone)
    ) {
      return this.requiredDocs.filter(doc =>
        ['tax-card', 'commercial-register'].includes(doc.id)
      );
    }

    return this.requiredDocs.filter(doc =>
      ['tax-card', 'commercial-register'].includes(doc.id)
    );
  }
  onUploadComplete(event: any, docId: string, preEstablishment: string) {
    if (this.isReadOnly) return; // Guard in readonly mode
    this.uploadedUrls[preEstablishment] = event.url || '';
    this.documentsForm.get(docId)?.setValue(event.url || '');
    this.documentsChanged.emit(this.uploadedUrls);
    console.log('✅ Uploaded:', docId, event.url);
  }

  onFileRemoved(docId: string, preEstablishment: string) {
    if (this.isReadOnly) return; // Guard in readonly mode
    delete this.uploadedUrls[preEstablishment];
    this.documentsForm.get(docId)?.reset();
    this.documentsChanged.emit(this.uploadedUrls);
    console.log('🗑️ Removed:', docId);
  }

}
